import {
  ActionType,
  ChangelogEntry,
  DeployLabelActionPayload,
  EntityType,
} from "@/queries/folder-entities/types";

// Action types that contain version numbers
const VERSION_NUMBER_ACTIONS = [
  ActionType.CREATE_VERSION,
  ActionType.CREATE_LABEL,
  ActionType.MOVE_LABEL,
  ActionType.DELETE_LABEL,
  ActionType.DEPLOY_LABEL,
] as const;

// Type-safe payload property extractors
export const getCommitMessage = (event: ChangelogEntry): string | null | undefined => {
  if (event.action_type !== ActionType.CREATE_VERSION) {
    return undefined;
  }
  return (event.action_payload as { commit_message?: string | null })?.commit_message;
};

export const getVersionNumber = (event: ChangelogEntry): number | undefined => {
  if (
    !VERSION_NUMBER_ACTIONS.includes(event.action_type as (typeof VERSION_NUMBER_ACTIONS)[number])
  ) {
    return undefined;
  }
  const payload = event.action_payload as {
    prompt_template_version_number?: number;
    workflow_version_number?: number;
    dataset_version_number?: number;
    tool_version_number?: number;
  };
  return (
    payload?.prompt_template_version_number ??
    payload?.workflow_version_number ??
    payload?.dataset_version_number ??
    payload?.tool_version_number
  );
};

export const getBatchName = (event: ChangelogEntry): string | undefined => {
  if (!event.action_payload || !("name" in event.action_payload)) {
    return undefined;
  }
  return event.action_payload.name as string | undefined;
};

export const getLabelName = (event: ChangelogEntry): string | undefined => {
  const labelActions = [
    ActionType.CREATE_LABEL,
    ActionType.MOVE_LABEL,
    ActionType.DELETE_LABEL,
    ActionType.DEPLOY_LABEL,
  ];

  if (!labelActions.includes(event.action_type)) {
    return undefined;
  }
  return (event.action_payload as { label_name?: string })?.label_name;
};

export const hasVersionNumber = (event: ChangelogEntry): boolean =>
  getVersionNumber(event) !== undefined;

export const getBatchRunId = (event: ChangelogEntry): number | undefined => {
  if (event.action_type !== ActionType.RUN_BATCH) {
    return undefined;
  }
  return (event.action_payload as { batch_run_id?: number })?.batch_run_id;
};

interface EntityNamePayload {
  prompt_template_name?: string;
  workflow_name?: string;
  dataset_group_name?: string;
  tool_name?: string;
}

export const getEntityNameFromPayload = (
  entityType: EntityType,
  payload: EntityNamePayload | null | undefined,
): string => {
  if (!payload) {
    return "";
  }

  switch (entityType) {
    case EntityType.PROMPT:
      return payload.prompt_template_name ?? "";
    case EntityType.WORKFLOW:
      return payload.workflow_name ?? "";
    case EntityType.DATASET:
      return payload.dataset_group_name ?? "";
    case EntityType.TOOL:
      return payload.tool_name ?? "";
    default:
      return "";
  }
};

export const getVersionEntityLink = (
  workspaceId: number | string,
  entityType: EntityType,
  entityId: string | number | null,
  version?: number,
): string | undefined => {
  if (!entityId) {
    return undefined;
  }

  const entityPathMap: Partial<Record<EntityType, string>> = {
    [EntityType.PROMPT]: "prompt",
    [EntityType.WORKFLOW]: "workflow",
    [EntityType.TOOL]: "tool",
  };
  const entityPath = entityPathMap[entityType] ?? "prompt";
  const basePath = `/workspace/${workspaceId}/${entityPath}/${entityId}`;

  return version !== undefined ? `${basePath}/version/${version}` : basePath;
};

interface MoveLabelVersionPayload {
  old_prompt_template_version_number?: number;
  new_prompt_template_version_number?: number;
  old_workflow_version_number?: number;
  new_workflow_version_number?: number;
  old_tool_version_number?: number;
  new_tool_version_number?: number;
}

export const getMoveLabelVersions = (
  entityType: EntityType,
  payload: MoveLabelVersionPayload | null | undefined,
): { fromVersion: number | undefined; toVersion: number | undefined } => {
  if (!payload) {
    return { fromVersion: undefined, toVersion: undefined };
  }

  if (entityType === EntityType.PROMPT) {
    return {
      fromVersion: payload.old_prompt_template_version_number,
      toVersion: payload.new_prompt_template_version_number,
    };
  }

  if (entityType === EntityType.TOOL) {
    return {
      fromVersion: payload.old_tool_version_number,
      toVersion: payload.new_tool_version_number,
    };
  }

  return {
    fromVersion: payload.old_workflow_version_number,
    toVersion: payload.new_workflow_version_number,
  };
};

// Deployment metadata extractors for DEPLOY_LABEL action
export const isDeployLabelAction = (event: ChangelogEntry): boolean =>
  event.action_type === ActionType.DEPLOY_LABEL;

export const getDeploymentMetadata = (
  event: ChangelogEntry,
): {
  originalChangelogId: number | undefined;
  originalActionType: "MOVE_LABEL" | "DELETE_LABEL" | undefined;
  deployedBy: number | undefined;
} => {
  if (!isDeployLabelAction(event)) {
    return {
      originalChangelogId: undefined,
      originalActionType: undefined,
      deployedBy: undefined,
    };
  }

  const payload = event.action_payload as DeployLabelActionPayload | null;

  return {
    originalChangelogId: payload?.original_changelog_id,
    originalActionType: payload?.original_action_type,
    deployedBy: payload?.deployed_by,
  };
};

export const getDeleteLabelVersion = (
  entityType: EntityType,
  payload:
    | {
        prompt_template_version_number?: number;
        workflow_version_number?: number;
        tool_version_number?: number;
      }
    | null
    | undefined,
): number | undefined => {
  if (!payload) {
    return undefined;
  }

  if (entityType === EntityType.PROMPT) {
    return payload.prompt_template_version_number;
  }

  if (entityType === EntityType.TOOL) {
    return payload.tool_version_number;
  }

  return payload.workflow_version_number;
};
